<template>
  <!-- Question label -->
  <div class="question-label">
    <span class="question-label__icon">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24l0 112c0 13.3-10.7 24-24 24s-24-10.7-24-24l0-112c0-13.3 10.7-24 24-24zm-32 224a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/></svg>
    </span>
    <span>{{ question }}</span>
  </div>

  <!-- Content bubble -->
  <div class="content-bubble">
    <img
      v-if="type === 'gambar'"
      :src="imageUrl"
      class="content-image"
      alt="Relatability content"
      @error="onImageError"
    />
    <p class="content-text">{{ content }}</p>
  </div>

  <!-- Vibe indicator badge -->
  <div class="vibe-badge" :class="vibeBadgeClass">
    <span class="vibe-badge__emoji">{{ currentThumbEmoji }}</span>
    <span class="vibe-badge__label">{{ vibeLabel }}</span>
  </div>

  <div class="slider-ui-wrapper">
    <div class="slider-container">
      <div class="slider-track"></div>
      <div class="slider-fill" :style="{ width: `${sliderValue}%`, backgroundColor: currentFillColor }"></div>
      <div class="slider-thumb" :style="{ left: `${sliderValue}%` }">{{ currentThumbEmoji }}</div>
      <input
        type="range"
        min="0"
        max="100"
        v-model.number="sliderValue"
        class="slider-input"
      />
    </div>
    <div class="labels-container">
      <button class="label-item" :class="{ 'label-item--active': sliderValue <= 20 }" @click="setSliderValue(10)">
        <span class="label-emoji">😐</span>
        <p>Nope</p>
      </button>
      <button class="label-item" :class="{ 'label-item--active': sliderValue > 20 && sliderValue <= 49 }" @click="setSliderValue(38)">
        <span class="label-emoji">😟</span>
        <p>Not really</p>
      </button>
      <button class="label-item" :class="{ 'label-item--active': sliderValue > 49 && sliderValue <= 80 }" @click="setSliderValue(63)">
        <span class="label-emoji">😎</span>
        <p>Pretty cool</p>
      </button>
      <button class="label-item" :class="{ 'label-item--active': sliderValue > 80 }" @click="setSliderValue(90)">
        <span class="label-emoji">🔥</span>
        <p>Totally me!</p>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  id: { type: Number, required: true },
  question: { type: String, required: true },
  type: { type: String, default: 'text' },
  content: { type: String, required: true },
  filename: { type: String, required: true },
  initialValue: { type: Number, default: 50 } // default ke tengah slider (50)
});

const emit = defineEmits(['update:value']);
const sliderValue = ref(props.initialValue);

watch(sliderValue, (newValue) => {
  emit('update:value', { id: props.id, value: newValue });
});

// [LOGIKA BARU] Computed property untuk membuat path gambar yang valid
const imageUrl = computed(() => {
  if (props.type === 'gambar' && props.content) {
    // console.log(props);
    try {
      // `require` akan memberitahu Webpack untuk menyertakan gambar ini dalam build.
      // Path ini bersifat RELATIF dari file ini (RelatabilitySlider.vue).
      // Asumsi:
      // - Komponen ini ada di: src/components/RelatabilitySlider.vue
      // - Gambar Anda ada di:  src/assets/nama-gambar.jpg
      // Maka path-nya adalah '../assets/'
      return require(`../assets/questions/${props.filename}`);
    } catch (e) {
      // console.error(`Gambar tidak ditemukan di assets: ${props.filename}`);
      // Jika gambar tidak ditemukan, kembalikan string kosong atau path ke gambar placeholder
      return ''; 
    }
  }
  return '';
});

// Fungsi untuk menangani jika gambar gagal dimuat (misalnya, path salah)
function onImageError(event) {
  // Hide the broken image instead of loading an external URL
  // (external URLs can fail with CORS/network errors on iOS Safari)
  event.target.style.display = 'none';
}

function setSliderValue(value) {
  sliderValue.value = value;
}

const currentThumbEmoji = computed(() => {
  const value = sliderValue.value;
  if (value <= 20) return '😐';
  if (value <= 49) return '😟';
  if (value <= 80) return '😎';
  return '🔥';
});

const vibeLabel = computed(() => {
  const v = sliderValue.value;
  if (v <= 20) return 'Nope, bukan aku banget';
  if (v <= 49) return 'Kurang menggambarkan aku';
  if (v <= 80) return 'Lumayan menggambarkan aku';
  return 'Ini banget aku!';
});

const vibeBadgeClass = computed(() => {
  const v = sliderValue.value;
  if (v <= 20) return 'vibe-badge--neutral';
  if (v <= 49) return 'vibe-badge--low';
  if (v <= 80) return 'vibe-badge--mid';
  return 'vibe-badge--high';
});

const currentFillColor = computed(() => {
  const v = sliderValue.value;
  if (v <= 49) return '#4489C6';
  if (v <= 79) return '#F0D25B';
  return '#DF654D';
});
</script>

<style scoped>
/* ---- Question label ---- */
.question-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: #EFF6FF;
  border: 1.5px solid #c3d9f0;
  border-radius: 50px;
  padding: 0.35rem 0.9rem;
  margin-bottom: 1.1rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #26488A;
  letter-spacing: 0.2px;
  width: fit-content;
  align-self: center;
}

.question-label__icon {
  display: flex;
  align-items: center;
}

.question-label__icon svg {
  width: 11px;
  height: 11px;
  fill: #4489C6;
}

/* ---- Content bubble ---- */
.content-bubble {
  background: linear-gradient(135deg, #f8fbff, #ffffff);
  border: 2px solid #dce9f5;
  border-radius: 20px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 16px rgba(38, 72, 138, 0.07);
  position: relative;
}

.content-bubble::before {
  content: '\201C';
  position: absolute;
  top: -8px;
  left: 18px;
  font-size: 3rem;
  color: #4489C6;
  opacity: 0.18;
  line-height: 1;
  font-family: Georgia, serif;
}

.content-image {
  max-width: 100%;
  max-height: 250px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.09);
}

.content-text {
  font-size: 1.15rem;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.55;
  text-align: center;
  margin: 0;
  font-style: italic;
}

/* ---- Vibe badge ---- */
.vibe-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 1rem;
  border-radius: 50px;
  font-size: 0.82rem;
  font-weight: 700;
  margin-bottom: 1.1rem;
  align-self: center;
  transition: background 0.3s ease, color 0.3s ease;
}

.vibe-badge__emoji { font-size: 1.1rem; }

.vibe-badge--neutral { background: #f0f0f0; color: #7f8c8d; }
.vibe-badge--low     { background: #e8f3fc; color: #2980B9; }
.vibe-badge--mid     { background: #fffbea; color: #a07c10; }
.vibe-badge--high    { background: #fff2ee; color: #c0392b; }

/* ---- Slider wrapper ---- */
.slider-ui-wrapper {
  width: 100%;
}

.slider-container {
  position: relative;
  width: 100%;
  height: 44px;
  display: flex;
  align-items: center;
}

.slider-track {
  position: absolute;
  background-color: #e2ecf7;
  width: 100%;
  height: 14px;
  border-radius: 8px;
  z-index: 1;
}

.slider-fill {
  position: absolute;
  height: 14px;
  border-radius: 8px;
  z-index: 2;
  transition: background-color 0.3s ease;
  opacity: 0.85;
}

.slider-thumb {
  position: absolute;
  font-size: 2.6rem;
  z-index: 4;
  pointer-events: none;
  line-height: 1;
  top: 50%;
  transform: translate(-50%, -50%);
  text-shadow: 0 2px 6px rgba(0,0,0,0.18);
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.1));
}

.slider-input {
  position: absolute;
  width: 100%;
  height: 20px;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
  z-index: 5;
}

.slider-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 44px;
}

.slider-input::-moz-range-thumb {
  width: 20px;
  height: 44px;
  background: transparent;
  border: none;
  border-radius: 0;
}

/* ---- Label buttons ---- */
.labels-container {
  display: flex;
  justify-content: space-between;
  gap: 0.35rem;
  margin-top: 1rem;
}

.label-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 0.5rem 0.25rem;
  background: #f4f8fc;
  border: 1.5px solid #dce9f5;
  border-radius: 12px;
  cursor: pointer;
  font-family: 'Epilogue', sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  color: #7d95b0;
  transition: all 0.2s ease;
}

.label-item:hover:not(.label-item--active) {
  border-color: #4489C6;
  background: #edf4fb;
  color: #26488A;
  transform: translateY(-2px);
}

.label-item--active {
  background: #26488A;
  border-color: #26488A;
  color: #ffffff;
  box-shadow: 0 4px 10px rgba(38, 72, 138, 0.25);
}

.label-emoji {
  font-size: 1.4rem;
  line-height: 1;
}

.label-item p { margin: 0; }

/* ---- Mobile ---- */
@media (max-width: 480px) {
  .content-text {
    font-size: 0.9rem;
  }
  .slider-thumb {
    font-size: 2.2rem;
  }
  .label-item {
    font-size: 0.65rem;
    padding: 0.4rem 0.1rem;
    border-radius: 10px;
  }
  .label-emoji {
    font-size: 1.2rem;
  }
  .vibe-badge {
    font-size: 0.75rem;
  }
}
</style>